import { createRouter, createWebHistory } from 'vue-router'
import { authReady, auth } from '@/firebase'
import { useUserStore } from '@/stores/user'

import Layout from '@/views/Layout.vue'
import Dashboard from '@/views/Dashboard.vue'
import Vote from '@/views/Vote.vue'
import Upload from '@/views/Upload.vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
  },
  {
    path: '/',
    component: Layout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { requiresAuth: true, requiresShareholder: true },
      },
      {
        path: '/settings',
        name: 'Settings',
        component: () => import('@/views/Settings.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/upload/:meeting_id',
        name: 'Upload',
        component: Upload,
        meta: { requiresAuth: true, requiresShareholder: true },
      },
      {
        path: '/vote/:meeting_id',
        name: 'Vote',
        component: Vote,
        meta: { requiresAuth: true, requiresShareholder: true },
      },
      {
        path: '/admin',
        component: () => import('@/views/admin/AdminLayout.vue'),
        redirect: '/admin/users',
        meta: { requiresAuth: true, requiresAdmin: true },
        children: [
          {
            path: 'users',
            name: 'AdminUsers',
            component: () => import('@/views/admin/Users.vue'),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
          {
            path: 'votes',
            name: 'AdminVotes',
            component: () => import('@/views/admin/Votes.vue'),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
          {
            path: 'shareholders',
            name: 'AdminShareholders',
            component: () => import('@/views/admin/Shareholders.vue'),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
          {
            path: 'meetings',
            name: 'AdminMeetings',
            component: () => import('@/views/admin/Meetings.vue'),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
          {
            path: 'ballot/:meeting_id',
            name: 'AdminBallotEditor',
            component: () => import('@/views/admin/BallotEditor.vue'),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
        ],
      },
      // Legacy obscure admin URL kept as a short-term redirect.
      {
        path: '/1x2793wzz1',
        redirect: '/admin',
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

async function ensureProfile() {
  const userStore = useUserStore()
  if (!userStore.profile.uid && auth.currentUser) {
    await userStore.fetchUserProfile(auth.currentUser)
  }
  return userStore
}

router.beforeEach(async (to) => {
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)
  const requiresAdmin = to.matched.some((r) => r.meta.requiresAdmin)
  const requiresShareholder = to.matched.some((r) => r.meta.requiresShareholder)

  await authReady

  if (requiresAuth && !auth.currentUser) {
    return { name: 'Login' }
  }

  if (requiresAuth && auth.currentUser) {
    const userStore = await ensureProfile()

    // Root path — redirect based on account type
    if (to.path === '/') {
      return userStore.isShareholder ? { name: 'Dashboard' } : { name: 'AdminUsers' }
    }

    if (requiresShareholder && !userStore.isShareholder) {
      if (userStore.isAdmin && to.query.test === 'true') {
        // Allow admin test mode through to vote/upload pages
      } else {
        return userStore.isAdmin ? { name: 'AdminUsers' } : { name: 'Login' }
      }
    }

    if (requiresAdmin && !userStore.isAdmin) {
      return userStore.isShareholder ? { name: 'Dashboard' } : { name: 'Login' }
    }
  }

  return true
})

export default router
